// resources/js/tracking/index.js
//
// Centralized Mixpanel client. Single entry point for all client-side
// analytics events. Mirrors app/Services/Tracking/TrackingService.php.
//
// Boot order:
//   1. tracking.init({ mixpanelToken, websiteContext, userContext, queuedEvents })
//   2. mixpanel.init() + register super properties
//   3. identify(user.id) if user (no alias() — modern Mixpanel pattern)
//   4. people.set(...) with profile + first-touch attribution
//   5. flush queuedEvents (events PHP queued for post-redirect firing)

import { captureFirstTouchIfMissing, getFirstTouch, deriveSignupSource } from './utm-attribution';

let initialized = false;
let websiteCtx = null;
let userCtx = null;
let mixpanelRef = null;
let mixpanelEnabled = false;

export const tracking = {
  init({ mixpanelInstance, mixpanelToken, websiteContext, userContext, queuedEvents }) {
    if (initialized) return;
    initialized = true;

    captureFirstTouchIfMissing();

    websiteCtx = websiteContext || {};
    userCtx = userContext || null;
    mixpanelRef = mixpanelInstance || (typeof mixpanel !== 'undefined' ? mixpanel : null);

    if (!mixpanelToken || !mixpanelRef) {
      mixpanelEnabled = false;
      return;
    }
    mixpanelEnabled = true;

    mixpanelRef.init(mixpanelToken, { debug: false });

    // Register super properties on every event (client-side analog of
    // TrackingService::getSuperProperties()).
    mixpanelRef.register(buildSuperProperties());

    if (userCtx && userCtx.id) {
      // identify with the integer user.id as distinct_id.
      // No alias() — Mixpanel handles anon→identified merging via identify alone.
      mixpanelRef.identify(String(userCtx.id));

      const firstTouch = getFirstTouch();
      mixpanelRef.people.set({
        $email: userCtx.email,
        $name: userCtx.name,
        $first_name: userCtx.name,
        website_id: websiteCtx.website_id,
        country: websiteCtx.country,
        locale: websiteCtx.locale,
        home_country: userCtx.home_country,
        home_region: userCtx.home_region,
        current_plan: userCtx.plan,
        signup_source: deriveSignupSource(firstTouch),
        signup_utm_source: firstTouch ? firstTouch.utm_source : null,
        signup_utm_medium: firstTouch ? firstTouch.utm_medium : null,
        signup_utm_campaign: firstTouch ? firstTouch.utm_campaign : null,
        last_login: new Date().toISOString(),
      });
    }

    // Persist distinct_id in cookie for server-side correlation.
    if (mixpanelRef.get_distinct_id) {
      document.cookie = 'mixpanel_distinct_id=' + mixpanelRef.get_distinct_id() + '; path=/';
    }

    // Flush queued events that PHP asked us to fire after identification.
    (queuedEvents || []).forEach((evt) => {
      this.track(evt.name, evt.properties || {}, evt.also_fire_old_name || null);
    });
  },

  /**
   * Core track method. Mirrors TrackingService::track().
   * Auto-applies super properties via mixpanel.register (set in init), plus an explicit
   * dual-fire of the same event under an old name during renaming transition.
   */
  track(eventName, properties = {}, dualFireOldName = null) {
    if (!mixpanelEnabled || !mixpanelRef) return;
    mixpanelRef.track(eventName, properties, { send_immediately: true });
    if (dualFireOldName) {
      mixpanelRef.track(dualFireOldName, properties, { send_immediately: true });
    }
  },

  /**
   * Page Viewed (renamed from Page View).
   * Fires both names during 2-week transition.
   */
  pageViewed(pageType, extras = {}) {
    this.track('Page Viewed', {
      page_type: pageType,
      url: window.location.href,
      title: document.title,
      ...extras,
    }, 'Page View');
  },

  /**
   * Product Category Selected (renamed from Product Category Updated).
   */
  productCategorySelected(categoryId) {
    this.track('Product Category Selected', {
      'Product Category': categoryId,
      category_id: categoryId,
    }, 'Product Category Updated');
  },

  /**
   * Map Opened — fired at the mount of a map component.
   */
  mapOpened({ mushroomType, mapVersion, isFirstTime, userPlanForItem, viewedRegion, viewedDept, entryPoint, sessionCount }) {
    this.track('Map Opened', {
      mushroom_type: mushroomType,
      map_version: mapVersion,
      is_first_time: isFirstTime,
      user_plan_for_item: userPlanForItem,
      viewed_region: viewedRegion,
      viewed_dept: viewedDept,
      entry_point: entryPoint,
      session_count: sessionCount,
    });
  },

  /**
   * Map Interaction — fired at the FIRST zoom or pan in a session (debounced by caller).
   */
  mapInteraction({ mushroomType, interactionType, userPlanForItem, viewedRegion, viewedDept }) {
    this.track('Map Interaction', {
      mushroom_type: mushroomType,
      interaction_type: interactionType,
      user_plan_for_item: userPlanForItem,
      viewed_region: viewedRegion,
      viewed_dept: viewedDept,
    });
  },

  /**
   * Mushroom Clicked — fired when a user clicks a mushroom marker / point.
   */
  mushroomClicked({ mushroomType, mushroomId, isLocked, userPlanForItem, viewedRegion, viewedDept, zoomLevel }) {
    this.track('Mushroom Clicked', {
      mushroom_type: mushroomType,
      mushroom_id: mushroomId,
      is_locked: isLocked,
      user_plan_for_item: userPlanForItem,
      viewed_region: viewedRegion,
      viewed_dept: viewedDept,
      zoom_level: zoomLevel,
    });
  },

  /**
   * Map Filter Applied — fired when a user changes a filter input.
   */
  mapFilterApplied({ mushroomType, filterType, filterValue, userPlanForItem }) {
    this.track('Map Filter Applied', {
      mushroom_type: mushroomType,
      filter_type: filterType,
      filter_value: filterValue,
      user_plan_for_item: userPlanForItem,
    });
  },

  /**
   * Map Session Returned — fired at Map Opened when session_count > 1.
   */
  mapSessionReturned({ mushroomType, userPlanForItem, sessionCount, daysSinceFirstOpen, daysSinceLastOpen }) {
    this.track('Map Session Returned', {
      mushroom_type: mushroomType,
      user_plan_for_item: userPlanForItem,
      session_count: sessionCount,
      days_since_first_open: daysSinceFirstOpen,
      days_since_last_open: daysSinceLastOpen,
    });
  },

  // ========== Phase 3: Monetization funnel ==========

  paywallShown({ paywallInstanceId, trigger, paywalledFeature, paywalledItem, userPlanForItem, viewedRegion, viewedDept, timeSinceRegisterDays, priorPaywallsInSession }) {
    this.track('Paywall Shown', {
      paywall_instance_id: paywallInstanceId,
      trigger: trigger,
      paywalled_feature: paywalledFeature,
      paywalled_item: paywalledItem,
      user_plan_for_item: userPlanForItem,
      viewed_region: viewedRegion,
      viewed_dept: viewedDept,
      time_since_register_days: timeSinceRegisterDays,
      prior_paywalls_in_session: priorPaywallsInSession,
    });
  },

  paywallCtaClicked({ paywallInstanceId, ctaLabel, timeToClickMs, paywalledItem }) {
    this.track('Paywall CTA Clicked', {
      paywall_instance_id: paywallInstanceId,
      cta_label: ctaLabel,
      time_to_click_ms: timeToClickMs,
      paywalled_item: paywalledItem,
    });
  },

  paywallDismissed({ paywallInstanceId, dismissMethod, timeVisibleMs }) {
    this.track('Paywall Dismissed', {
      paywall_instance_id: paywallInstanceId,
      dismiss_method: dismissMethod,
      time_visible_ms: timeVisibleMs,
    });
  },

  plansViewed({ entryPoint, referrerPaywallInstanceId, displayedCategories }) {
    this.track('Plans Viewed', {
      entry_point: entryPoint,
      referrer_paywall_instance_id: referrerPaywallInstanceId,
      displayed_categories: displayedCategories,
    });
  },

  productViewed({ productId, productSlug, productRecurrent, productPrice, productCurrency, productCategoryId }) {
    this.track('Product Viewed', {
      product_id: productId,
      product_slug: productSlug,
      product_recurrent: productRecurrent,
      product_price: productPrice,
      product_currency: productCurrency,
      product_category_id: productCategoryId,
    });
  },

  // ========== Phase 3: Acquisition detail ==========

  landingCtaClicked({ landingSlug, ctaId, ctaPosition }) {
    this.track('Landing CTA Clicked', {
      landing_slug: landingSlug,
      cta_id: ctaId,
      cta_position: ctaPosition,
    });
  },

  registrationFormViewed({ entryPoint, intendedProduct }) {
    this.track('Registration Form Viewed', {
      entry_point: entryPoint,
      intended_product: intendedProduct,
    });
  },

  registrationStarted({ entryPoint, authMethod }) {
    this.track('Registration Started', {
      entry_point: entryPoint,
      auth_method: authMethod,
    });
  },

  registrationSubmitted({ entryPoint, authMethod }) {
    this.track('Registration Submitted', {
      entry_point: entryPoint,
      auth_method: authMethod,
    });
  },

  registrationFailed({ entryPoint, authMethod, failureReason }) {
    this.track('Registration Failed', {
      entry_point: entryPoint,
      auth_method: authMethod,
      failure_reason: failureReason,
    });
  },

  /**
   * Reset on logout — clears distinct_id and starts fresh anonymous tracking.
   */
  reset() {
    if (mixpanelEnabled && mixpanelRef && mixpanelRef.reset) {
      mixpanelRef.reset();
    }
  },
};

function buildSuperProperties() {
  const props = { ...websiteCtx };
  if (userCtx) {
    props.user_id = userCtx.id;
    props.user_plan = userCtx.plan;
    props.user_home_country = userCtx.home_country;
    props.user_home_region = userCtx.home_region;
  }
  return props;
}

// Expose globally for inline scripts and Livewire listeners that can't import.
// Guard: only set once. app.js initialises tracking and sets this; subsequent
// bundles (mapV5.js etc.) that also import tracking must NOT overwrite the
// already-initialised instance with their own uninitialised copy.
if (typeof window !== 'undefined' && !window.MHTracking) {
  window.MHTracking = tracking;
}
